import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'

import { httpOk, FILE } from 'helpers/api'
import { formatDate, formatMoney, notify } from 'helpers/helpers'

/** Максимальная сторона фото перед отправкой — на бэке лимит 15 МБ, а снимки с телефона тяжелее. */
const MAX_IMAGE_SIDE = 2000
/** Всё, что тяжелее, пережимаем даже если по размеру картинка небольшая. */
const MAX_IMAGE_BYTES = 4 * 1024 * 1024
/** Окно поиска похожих приходов, дней в каждую сторону от даты накладной. */
const SEARCH_WINDOW_DAYS = 14

/** Ссылка на картинку: обычный адрес качает бэк, base64 разбираем на месте. */
function isImageSource(value) {
	return /^(https?:\/\/|data:image\/)/i.test(String(value ?? '').trim())
}

/** Вставленная base64-картинка — превращаем в файл, чтобы не гонять её строкой. */
function dataUrlToFile(dataUrl) {
	try {
		const [header, base64] = dataUrl.split(',')
		const mime = header.match(/data:(.*?);/)?.[1] || 'image/jpeg'
		const binary = atob(base64)
		const bytes = new Uint8Array(binary.length)
		for (let i = 0; i < binary.length; i++) {
			bytes[i] = binary.charCodeAt(i)
		}
		return new File([bytes], 'invoice.jpg', { 'type': mime })
	} catch (error) {
		return null
	}
}

/**
 * Ужимаем фото прямо в браузере — так же, как это делает мобилка перед отправкой.
 * Если что-то пошло не так (например, браузер не прочитал картинку) — шлём оригинал.
 */
function compressImage(file) {
	return new Promise((resolve) => {
		if (file.size <= MAX_IMAGE_BYTES && file.type === 'image/jpeg') {
			resolve(file)
			return
		}

		const url = URL.createObjectURL(file)
		const image = new Image()

		image.onload = () => {
			try {
				const scale = Math.min(1, MAX_IMAGE_SIDE / Math.max(image.width, image.height))
				const canvas = document.createElement('canvas')
				canvas.width = Math.round(image.width * scale)
				canvas.height = Math.round(image.height * scale)
				canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height)
				canvas.toBlob((blob) => {
					URL.revokeObjectURL(url)
					resolve(blob ? new File([blob], 'invoice.jpg', { 'type': 'image/jpeg' }) : file)
				}, 'image/jpeg', 0.85)
			} catch (error) {
				URL.revokeObjectURL(url)
				resolve(file)
			}
		}
		image.onerror = () => {
			URL.revokeObjectURL(url)
			resolve(file)
		}
		image.src = url
	})
}

/**
 * Документ по фото: накладную можно загрузить файлом, перетащить в окно, вставить из буфера
 * или дать ссылку. В приходе показываем ещё и похожие приходы — вдруг документ уже создан.
 * В перемещениях (mode="transfer") нужны только позиции: поставщика, цены и поиск
 * совпадений не показываем и на бэке не делаем.
 */
function PhotoScanModal({ show, posId, mode = 'reception', onClose, onApply }) {
	const { t } = useTranslation()

	// Приход ищет похожие документы и показывает поставщика; перемещение — только позиции и количество
	const isReception = mode === 'reception'
	const showPrices = mode !== 'transfer'
	const texts = {
		'reception': { 'title': 'reception_by_photo', 'hint': 'scan_invoice_hint', 'fill': 'fill_reception' },
		'transfer': { 'title': 'transfer_by_photo', 'hint': 'scan_transfer_hint', 'fill': 'fill_transfer' },
	}[mode] ?? { 'title': 'reception_by_photo', 'hint': 'scan_invoice_hint', 'fill': 'fill_reception' }

	const inputRef = useRef()
	// Запрос стартует и из обработчика вставки, где актуального стейта не видно
	const loadingRef = useRef(false)

	const [loading, setLoading] = useState(false)
	const [searched, setSearched] = useState(false)
	const [failed, setFailed] = useState(false)
	const [dragOver, setDragOver] = useState(false)
	const [imageUrl, setImageUrl] = useState('')
	const [recognized, setRecognized] = useState(null)
	const [matches, setMatches] = useState([])
	const [message, setMessage] = useState('')

	const isInvoice = !!recognized && recognized.is_invoice === true

	function resetState() {
		loadingRef.current = false
		setLoading(false)
		setSearched(false)
		setFailed(false)
		setDragOver(false)
		setImageUrl('')
		setRecognized(null)
		setMatches([])
		setMessage('')
	}

	/** Единая точка распознавания: либо файл, либо ссылка — качает её уже бэк. */
	const searchByImage = useCallback(async ({ file, url }) => {
		if (loadingRef.current) return

		loadingRef.current = true
		setSearched(false)
		setFailed(false)
		setRecognized(null)
		setMatches([])
		setMessage('')
		setLoading(true)

		const formData = new FormData()
		if (file) {
			const image = await compressImage(file)
			formData.append('image', image, image.name || 'invoice.jpg')
		} else {
			formData.append('image_url', url)
		}
		formData.append('pos_id', posId)
		formData.append('days', SEARCH_WINDOW_DAYS)

		if (!isReception) formData.append('only_recognize', 1)

		const response = await FILE(!isReception ? '/documents-recognize-by-image' : '/documents-in-search-by-image', formData)

		loadingRef.current = false
		setLoading(false)
		// Ошибку запроса уже показал перехватчик — не подменяем её «это не накладная»
		if (!httpOk(response)) {
			setFailed(true)
			return
		}

		setSearched(true)
		setRecognized(response.data?.recognized ?? null)
		setMatches(response.data?.matches ?? [])
		setMessage(response.data?.message ?? '')
	}, [posId, isReception])

	const handleFile = useCallback((file) => {
		if (!file) return
		if (!file.type.startsWith('image/')) {
			notify(t('invalid_photo'))
			return
		}
		searchByImage({ 'file': file })
	}, [searchByImage]) // eslint-disable-line react-hooks/exhaustive-deps

	const handleUrl = useCallback((value) => {
		const url = String(value ?? '').trim()
		if (!isImageSource(url)) {
			notify(t('invalid_photo'))
			return
		}

		setImageUrl(url)
		if (url.startsWith('data:')) {
			handleFile(dataUrlToFile(url))
			return
		}
		searchByImage({ 'url': url })
	}, [handleFile, searchByImage]) // eslint-disable-line react-hooks/exhaustive-deps

	function handleSelectFile(e) {
		const file = e.target.files?.[0]
		if (inputRef.current) inputRef.current.value = ''
		handleFile(file)
	}

	const handleDropData = useCallback((dataTransfer) => {
		setDragOver(false)

		const file = dataTransfer?.files?.[0]
		if (file) {
			handleFile(file)
			return
		}

		// Картинку перетащили с другой вкладки — там не файл, а ссылка на неё
		const url = dataTransfer?.getData('text/uri-list') || dataTransfer?.getData('text/plain')
		if (url) handleUrl(url)
	}, [handleFile, handleUrl])

	function handleApply() {
		onApply?.(recognized)
		onClose?.()
	}

	// Итог с бэка приходит не всегда — тогда считаем его сами по позициям
	function countTotal() {
		if (recognized?.total_amount) return Number(recognized.total_amount)

		var total = 0
		for (let i = 0; i < (recognized?.items?.length ?? 0); i++) {
			total += Number(recognized.items[i]['quantity']) * Number(recognized.items[i]['price'])
		}
		return total
	}

	// Ctrl+V ловим на всём окне — целиться в конкретное поле человек не обязан
	useEffect(() => {
		if (!show) return

		function handlePaste(e) {
			const items = e.clipboardData?.items ?? []
			for (let i = 0; i < items.length; i++) {
				if (items[i].kind === 'file') {
					e.preventDefault()
					handleFile(items[i].getAsFile())
					return
				}
			}

			const text = e.clipboardData?.getData('text') ?? ''
			if (isImageSource(text)) {
				e.preventDefault()
				handleUrl(text)
			}
		}

		document.addEventListener('paste', handlePaste)
		return () => document.removeEventListener('paste', handlePaste)
	}, [show, handleFile, handleUrl])

	// Перетаскивание ловим на всём окне: мимо модалки браузер просто открыл бы файл,
	// а вместе с ним — потерял бы уже заполненную форму прихода
	useEffect(() => {
		if (!show) return

		function handleWindowDragOver(e) {
			e.preventDefault()
		}

		function handleWindowDrop(e) {
			e.preventDefault()
			handleDropData(e.dataTransfer)
		}

		window.addEventListener('dragover', handleWindowDragOver)
		window.addEventListener('drop', handleWindowDrop)
		return () => {
			window.removeEventListener('dragover', handleWindowDragOver)
			window.removeEventListener('drop', handleWindowDrop)
		}
	}, [show, handleDropData])

	useEffect(() => {
		if (!show) resetState()
	}, [show]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<Modal show={show} animation={false} centered size="lg"
			onHide={onClose}>
			<Modal.Header closeButton>
				<Modal.Title>{t(texts.title)}</Modal.Title>
			</Modal.Header>
			{/* Высота фиксированная: позиций в накладной бывает много, но окно от этого прыгать не должно */}
			<Modal.Body
				style={{ 'height': '500px', 'display': 'flex', 'flexDirection': 'column' }}
				onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
				onDragLeave={(e) => {
					// Переход на вложенный элемент — это не уход из окна, подсветку не гасим
					if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false)
				}}>
				<input
					id="actual-btn-documents-in-photo"
					ref={inputRef}
					hidden
					type="file"
					onChange={handleSelectFile}
					accept="image/jpeg, image/png, image/webp"
				/>

				{/* Скроллится только распознанное, ссылка и кнопки всегда на виду */}
				<div className="flex-grow-1" style={{ 'overflowY': 'auto', 'overflowX': 'hidden' }}>
					{loading &&
						<div className="text-center py-5">
							<span className="spinner-border" role="status"></span>
							<p className="fw-600 mt-3 mb-1">{t('recognizing_invoice')}</p>
							<p className="text-muted mb-0">{t('recognizing_invoice_hint')}</p>
						</div>
					}

					{(!loading && !searched && !failed) &&
						<div className="text-center h-100 d-flex flex-column justify-content-center align-items-center"
							style={{
								'border': `2px dashed ${dragOver ? '#4a7dff' : 'var(--dark-border, #d0d5dd)'}`,
								'borderRadius': '8px',
								'background': dragOver ? 'rgba(74, 125, 255, 0.06)' : 'transparent',
							}}>
							<i className="uil uil-capture" style={{ 'fontSize': '48px' }}></i>
							<p className="fw-600 mt-2 mb-1">{t(texts.hint)}</p>
							<p className="text-muted">{t('photo_source_hint')}</p>
							<label htmlFor="actual-btn-documents-in-photo" className="btn btn-primary btn-rounded btn-wide mb-0">
								{t('select_photo')}
							</label>
						</div>
					}

					{(!loading && failed) &&
						<div className="alert alert-danger">
							<div className="fw-600">{t('error')}</div>
							<div>{t('try_again')}</div>
						</div>
					}

					{(!loading && searched && !isInvoice) &&
						<div className="alert alert-danger">
							<div className="fw-600">{message ? message : t('invoice_not_recognized')}</div>
							<div>{t('invoice_not_recognized_hint')}</div>
						</div>
					}

					{(!loading && isInvoice) &&
						<>
							<div className="card mb-3">
								<div className="card-body">
									<h6 className="fw-600">{t('recognized_data')}</h6>
									{/* Пустые поля не показываем — прочерки только мешают */}
									{(isReception && !!recognized.supplier_name) &&
										<div className="d-flex justify-content-between">
											<span className="text-muted">{t('supplier')}</span>
											<span>{recognized.supplier_name}</span>
										</div>
									}
									{(isReception && !!recognized.supplier_inn) &&
										<div className="d-flex justify-content-between">
											<span className="text-muted">{t('inn')}</span>
											<span>{recognized.supplier_inn}</span>
										</div>
									}
									{!!recognized.document_number &&
										<div className="d-flex justify-content-between">
											<span className="text-muted">{t('document_number')}</span>
											<span>{recognized.document_number}</span>
										</div>
									}
									{!!recognized.document_date &&
										<div className="d-flex justify-content-between">
											<span className="text-muted">{t('date')}</span>
											<span>{formatDate(recognized.document_date)}</span>
										</div>
									}

									{recognized.items?.length > 0 &&
										<div className="table-responsive mt-2">
											<table className="table table-bordered mb-0">
												<thead>
													<tr>
														<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
														<th style={{ 'width': '100%' }}>{t('name2')}</th>
														<th style={{ 'minWidth': '100px' }} className="text-end">{t('quantity')}</th>
														{showPrices &&
															<>
																<th style={{ 'minWidth': '120px' }} className="text-end">{t('price')}</th>
																<th style={{ 'minWidth': '120px' }} className="text-end">{t('amount')}</th>
															</>
														}
													</tr>
												</thead>
												<tbody>
													{recognized.items.map((item, index) => (
														<tr key={index}>
															<td className="text-center">{index + 1}</td>
															<td className="td-ellipsis">{item.name}</td>
															<td className="text-end">{formatMoney(item.quantity, 2)}</td>
															{showPrices &&
																<>
																	<td className="text-end">{formatMoney(item.price)}</td>
																	<td className="text-end">
																		{formatMoney(Number(item.quantity) * Number(item.price))}
																	</td>
																</>
															}
														</tr>
													))}
												</tbody>
											</table>
										</div>
									}

									{showPrices &&
										<div className="d-flex justify-content-between fw-600 mt-2">
											<span>{t('total_amount')}</span>
											<span>{formatMoney(countTotal())}</span>
										</div>
									}
								</div>
							</div>

							{matches.length > 0 &&
								<>
									<div className="alert alert-warning">
										<div className="fw-600">{t('similar_receptions_found')}</div>
										<div>{t('similar_receptions_hint')}</div>
									</div>

									<div className="table-responsive mb-3">
										<table className="table table-bordered mb-0">
											<thead>
												<tr>
													<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
													<th style={{ 'width': '100%' }}>{t('supplier')}</th>
													<th style={{ 'minWidth': '120px' }}>{t('document_number')}</th>
													<th style={{ 'minWidth': '100px' }}>{t('date')}</th>
													<th style={{ 'minWidth': '100px' }}>{t('wms')}</th>
													<th style={{ 'minWidth': '120px' }} className="text-end">{t('amount')}</th>
													<th style={{ 'minWidth': '80px' }} className="text-end">%</th>
												</tr>
											</thead>
											<tbody>
												{matches.map((item, index) => (
													<tr key={index}>
														<td className="text-center">{index + 1}</td>
														<td className="td-ellipsis">
															<a href={`/documents-in/preview/${item.id}`} target="_blank" rel="noreferrer">
																{item.supplier_name}
															</a>
														</td>
														<td>{item.document_number}</td>
														<td>{formatDate(item.created_at)}</td>
														<td>{item.wms_name}</td>
														<td className="text-end">{formatMoney(item.total_amount)}</td>
														<td className="text-end">
															<span className={`${Number(item.score) >= 70 ? 'bg-success' : 'bg-warning'} text-white px-2`}>
																{Number(item.score)}%
															</span>
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</>
							}
						</>
					}
				</div>

				{!loading &&
					<div className="form-group mt-3">
						<label>{t('photo_url')}</label>
						<div className="d-flex gap-2">
							<input type="text" className="form-control"
								placeholder="https://"
								value={imageUrl}
								onChange={(e) => setImageUrl(e.target.value)}
								onKeyUp={(e) => {
									if (e.key === 'Enter') handleUrl(imageUrl)
								}} />
							<button type="button" className="btn btn-outline-primary text-nowrap"
								disabled={!imageUrl}
								onClick={() => handleUrl(imageUrl)}>
								{t('recognize')}
							</button>
						</div>
					</div>
				}

				{(!loading && (searched || failed)) &&
					<div className="d-flex gap-2 w-100">
						<label htmlFor="actual-btn-documents-in-photo" className="btn btn-warning w-100 mb-0">
							{t('another_photo')}
						</label>
						{isInvoice &&
							<button type="button" className="btn btn-primary w-100"
								onClick={handleApply}>
								{t(texts.fill)}
							</button>
						}
					</div>
				}
			</Modal.Body>
		</Modal>
	)
}

export default PhotoScanModal
